import React, { createContext, useContext, useState, useEffect } from 'react';

const ShopContext = createContext();

export const useShop = () => useContext(ShopContext);

export const ShopProvider = ({ children }) => {
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [region, setRegion] = useState('IN'); // 'IN' or 'INTL'
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Currency configuration
    const currencyConfig = {
        IN: { code: 'INR', symbol: '₹', rate: 1 },
        INTL: { code: 'USD', symbol: '$', rate: 0.012 }
    };

    const currentCurrency = currencyConfig[region];

    useEffect(() => {
        fetch('/api/products')
            .then(res => res.json())
            .then(data => {
                setProducts(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to fetch products", err);
                setLoading(false);
            });
    }, []);

    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
        setIsCartOpen(true);
    };

    const removeFromCart = (productId) => {
        setCart(prev => prev.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, change) => {
        setCart(prev => prev.map(item => {
            if (item.id === productId) {
                const newQty = Math.max(0, item.quantity + change);
                return newQty === 0 ? null : { ...item, quantity: newQty };
            }
            return item;
        }).filter(Boolean));
    };

    const toggleRegion = () => {
        setRegion(prev => prev === 'IN' ? 'INTL' : 'IN');
    };

    const cartTotal = cart.reduce((total, item) => {
        const price = region === 'IN' ? item.priceInr : item.priceUsd;
        return total + (price * item.quantity);
    }, 0);

    const placeOrder = async (userDetails) => {
        try {
            const response = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cart, region, userDetails })
            });

            const data = await response.json();
            if (response.ok) {
                setCart([]); // Clear cart
                return { success: true, message: data.message };
            } else {
                return { success: false, message: data.message };
            }
        } catch (error) {
            return { success: false, message: "Network error" };
        }
    };

    return (
        <ShopContext.Provider value={{
            products,
            cart,
            addToCart,
            removeFromCart,
            updateQuantity,
            isCartOpen,
            setIsCartOpen,
            region,
            toggleRegion,
            currency: currentCurrency,
            cartTotal,
            isCheckoutOpen,
            setIsCheckoutOpen,
            placeOrder,
            loading
        }}>
            {children}
        </ShopContext.Provider>
    );
};
